import { API_PATH, BASE_PATH } from './config';

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Box,
    Container,
    Typography,
    Paper,
    Card,
    CardContent,
    CircularProgress,
    Alert,
    Button,
    Grid
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

const ViewTripItinerary = () => {
    const { tripId } = useParams();
    const navigate = useNavigate();
    const [tripData, setTripData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (tripId) {
            fetchTripData();
        } else {
            setError('No trip ID provided in URL');
            setLoading(false);
        }
        // eslint-disable-next-line
    }, [tripId]);

    const fetchTripData = async () => {
        try {
            setLoading(true);
            setError(null);
            const url = `${API_PATH}/admin/get-trip-details.php?trip_id=${tripId}`;
            const response = await fetch(url);
            const text = await response.text();
            let result;
            try {
                result = JSON.parse(text);
            } catch (e) {
                setError('Invalid JSON response from server');
                setLoading(false);
                return;
            }
            if (result.success && result.trip) {
                setTripData(result.trip);
                setError(null);
            } else {
                setError(result.error || 'No trip data received');
            }
        } catch (err) {
            setError('Error: ' + err.message);
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', bgcolor: '#f5f5f5' }}>
                <Box sx={{ textAlign: 'center' }}>
                    <CircularProgress size={60} sx={{ mb: 2 }} />
                    <Typography variant="body1" color="text.secondary">
                        Loading trip itinerary...
                    </Typography>
                </Box>
            </Box>
        );
    }

    if (error) {
        return (
            <Container maxWidth="md" sx={{ py: 4, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
                <Box sx={{ mb: 3 }}>
                    <Button
                        startIcon={<ArrowBackIcon />}
                        onClick={() => navigate(-1)}
                    >
                        Back
                    </Button>
                </Box>
                <Alert severity="error" sx={{ p: 3 }}>
                    <Typography variant="h6" gutterBottom>
                        Error Loading Trip
                    </Typography>
                    <Typography variant="body2">
                        {error}
                    </Typography>
                </Alert>
            </Container>
        );
    }

    // Parse route data if it exists
    let itinerary = [];
    if (tripData?.route) {
        itinerary = Array.isArray(tripData.route) ? tripData.route : [];
    }
    // Activities per day from backend
    const activitiesByDay = tripData?.activities || {};

    const tripTitle = tripData?.name || 'Trip Itinerary';
    const dateRange = tripData?.start_date && tripData?.end_date 
        ? `${new Date(tripData.start_date).toLocaleDateString()} - ${new Date(tripData.end_date).toLocaleDateString()}`
        : 'Flexible Dates';

    return (
        <Container maxWidth="lg" sx={{ py: 4 }}>
            {/* Back Button */}
            <Box sx={{ mb: 3 }}>
                <Button
                    startIcon={<ArrowBackIcon />}
                    onClick={() => navigate(-1)}
                >
                    Back
                </Button>
            </Box>

            {/* Header */}
            <Paper sx={{ p: 4, mb: 4, bgcolor: 'primary.main', color: 'white' }}>
                <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold' }}>
                    {tripTitle}
                </Typography>
                <Typography variant="h6">
                    {dateRange}
                </Typography>
                {tripData?.start_location && (
                    <Typography variant="body1" sx={{ mt: 1 }}>
                        📍 {tripData.start_location}
                        {tripData.end_location && ` → ${tripData.end_location}`}
                    </Typography>
                )}
            </Paper>

            {/* Trip Info Cards */}
            {tripData && (
                <Grid container spacing={2} sx={{ mb: 4 }}>
                    {tripData.duration_days > 0 && (
                        <Grid item xs={12} sm={6} md={3}>
                            <Card>
                                <CardContent sx={{ textAlign: 'center' }}>
                                    <Typography variant="h5" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                                        {tripData.duration_days}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Days
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                    )}
                    {tripData.travel_pace && (
                        <Grid item xs={12} sm={6} md={3}>
                            <Card>
                                <CardContent sx={{ textAlign: 'center' }}>
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                        Travel Pace
                                    </Typography>
                                    <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                                        {tripData.travel_pace}
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                    )}
                    {tripData.status && (
                        <Grid item xs={12} sm={6} md={3}>
                            <Card>
                                <CardContent sx={{ textAlign: 'center' }}>
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                        Status
                                    </Typography>
                                    <Typography variant="h6" sx={{ fontWeight: 'bold', textTransform: 'capitalize' }}>
                                        {tripData.status}
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                    )}
                </Grid>
            )}

            {/* Itinerary */}
            {itinerary.length > 0 ? (
                <Grid container spacing={3}>
                    {itinerary.map((day, index) => {
                        const dayNum = day.dayNum || index + 1;
                        const dayActivities = activitiesByDay[dayNum] || [];
                        return (
                            <Grid item xs={12} sm={6} md={4} key={index}>
                                <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                                    <CardContent sx={{ flexGrow: 1 }}>
                                        {/* Day Badge */}
                                        <Box sx={{
                                            display: 'inline-block',
                                            bgcolor: 'primary.main',
                                            color: 'white',
                                            px: 2,
                                            py: 0.5,
                                            borderRadius: 1,
                                            mb: 2,
                                            fontWeight: 'bold'
                                        }}>
                                            Day {dayNum}
                                        </Box>

                                        {/* Date */}
                                        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                                            {day.formatted_date || day.actual_date || 'TBA'}
                                        </Typography>

                                        {/* Location */}
                                        <Typography variant="h6" sx={{ mb: 1, fontWeight: 'bold' }}>
                                            {day.city || 'Unknown Location'}
                                        </Typography>

                                        {/* Country */}
                                        {day.country && (
                                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                                                {day.country}
                                            </Typography>
                                        )}

                                        {/* Activities */}
                                        {dayActivities.length > 0 && (
                                            <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid #eee' }}>
                                                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                                                    ✓ {dayActivities.length} Activities
                                                </Typography>
                                                <Box component="ul" sx={{ pl: 2, m: 0 }}>
                                                    {dayActivities.map((activity, actIndex) => (
                                                        <Box key={actIndex} sx={{ mb: 1 }}>
                                                            <Typography
                                                                component="li"
                                                                variant="body2"
                                                                sx={{ fontWeight: 500 }}
                                                            >
                                                                {activity.name}
                                                            </Typography>
                                                            {activity.start_time && (
                                                                <Typography
                                                                    variant="caption"
                                                                    color="text.secondary"
                                                                    sx={{ pl: 2 }}
                                                                >
                                                                    🕐 {activity.start_time.substring(0, 5)}
                                                                </Typography>
                                                            )}
                                                            {activity.location && (
                                                                <Typography
                                                                    variant="caption"
                                                                    color="text.secondary"
                                                                    sx={{ pl: 2, display: 'block' }}
                                                                >
                                                                    📍 {activity.location}
                                                                </Typography>
                                                            )}
                                                            {activity.description && (
                                                                <Typography
                                                                    variant="caption"
                                                                    color="text.secondary"
                                                                    sx={{ pl: 2, display: 'block' }}
                                                                >
                                                                    {activity.description}
                                                                </Typography>
                                                            )}
                                                        </Box>
                                                    ))}
                                                </Box>
                                            </Box>
                                        )}
                                    </CardContent>
                                </Card>
                            </Grid>
                        );
                    })}
                </Grid>
            ) : (
                <Paper sx={{ p: 4, textAlign: 'center' }}>
                    <Typography color="text.secondary">
                        No itinerary details available for this trip.
                    </Typography>
                </Paper>
            )}
        </Container>
    );
};


export default ViewTripItinerary;
